import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BookingCalendar from '@/components/feature/BookingCalendar';
import { useSEO } from '@/hooks/useSEO';
import { supabase } from '@/lib/supabase';
import { TrackingOrder, TRACKING_STAGES } from './stages';
import TrackingTimeline from './components/TrackingTimeline';
import CompletedReward from './components/CompletedReward';

const SCHEDULABLE_STATUSES = ['em_transporte', 'pronto_entrega'];

interface OrderDetails {
  id: number;
  status: string;
  payment_terms: string | null;
  customer_notes: string | null;
  delivery_deadline: string | null;
  salesperson: string | null;
  subtotal_items: number | null;
  created_at: string;
  order_items: {
    id: number;
    product_id: string;
    product_name: string;
    quantity: number;
    unit_price: number;
    subtotal: number | null;
  }[];
}

function formatDocument(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const STATUS_LABELS: Record<string, string> = {
  pedido_recebido: 'Pedido recebido',
  enviado_fabrica: 'Enviado para a fábrica',
  finalizado_fabrica: 'Finalizado na fábrica',
  em_transporte: 'Em transporte',
  pronto_entrega: 'Pronto para entrega',
  entregue_cliente: 'Entregue ao cliente',
};

export default function RastreamentoPage() {
  const [documento, setDocumento] = useState('');
  const [senha, setSenha] = useState('');
  const [orders, setOrders] = useState<TrackingOrder[]>([]);
  const [orderDetails, setOrderDetails] = useState<Record<number, OrderDetails>>();
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useSEO({
    title: 'Acompanhe seu Pedido | Home Office Premium – Caruaru PE',
    description: 'Acompanhe o status do seu pedido de móveis corporativos em tempo real. Informe seu CPF ou CNPJ e veja em qual etapa está a produção e a entrega. Home Office Premium, Caruaru PE.',
    keywords: 'acompanhar pedido móveis, rastrear pedido Caruaru, status pedido móveis corporativos, Home Office Premium, rastreamento compra',
    path: '/rastreamento',
    ogTitle: 'Acompanhe seu Pedido | Home Office Premium',
    ogDescription: 'Informe seu CPF ou CNPJ e acompanhe cada etapa da produção e da entrega dos seus móveis.'
  });

  const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const digits = documento.replace(/\D/g, '');
    if (digits.length < 11) {
      setError('Digite um CPF ou CNPJ válido.');
      setOrders([]);
      setOrderDetails({});
      setSearched(true);
      return;
    }
    if (senha.trim().length !== 6) {
      setError('Digite a senha de acesso com 6 dígitos.');
      setOrders([]);
      setOrderDetails({});
      setSearched(true);
      return;
    }
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('tracking-lookup', {
        body: { cpf_cnpj: digits, access_code: senha.trim() },
      });
      if (invokeErr) throw invokeErr;

      const parsed = data as {
        ok?: boolean;
        orders?: TrackingOrder[];
        order_details?: Record<number, OrderDetails>;
        error?: string;
      };

      if (parsed?.error) {
        setOrders([]);
        setOrderDetails({});
        setError(parsed.error);
        return;
      }

      const trackingOrders = parsed?.orders ?? [];
      setOrders(trackingOrders);
      setOrderDetails(parsed?.order_details ?? {});
    } catch {
      setOrders([]);
      setOrderDetails({});
      setError('Não foi possível consultar agora. Tente novamente em instantes.');
    } finally {
      setLoading(false);
    }
  };

  const hasResults = orders.length > 0;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-20">
        {/* Hero */}
        <div className="relative bg-hop-graphite overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '28px 28px' }} />
          <div className="relative max-w-7xl mx-auto px-6 py-14 md:py-20 text-center">
            <span className="inline-block bg-[#E8610A]/15 text-[#F47A20] text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-5">
              Acompanhe seu pedido
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Onde está seu pedido?
            </h1>
            <p className="text-white/60 text-base max-w-2xl mx-auto leading-relaxed">
              Informe seu <strong className="text-white">CPF ou CNPJ</strong> e a{' '}
              <strong className="text-white">senha de acesso</strong> e acompanhe cada
              etapa — da fábrica até a entrega. Sem ligação, sem espera.
            </p>
          </div>
        </div>

        {/* Busca */}
        <div className="max-w-3xl mx-auto px-6 -mt-8 relative z-10">
          <form
            onSubmit={handleSearch}
            className="bg-white rounded-2xl border border-hop-border p-6 md:p-8 shadow-lg shadow-black/5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  CPF ou CNPJ
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={documento}
                  onChange={(e) => setDocumento(formatDocument(e.target.value))}
                  placeholder="000.000.000-00"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Senha de acesso
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value.replace(/\D/g, ''))}
                  placeholder="6 dígitos"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-800 tracking-widest focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3.5 rounded-xl hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Consultando...
                </>
              ) : (
                <>
                  <i className="ri-search-line"></i>
                  Acompanhar
                </>
              )}
            </button>
            <p className="text-xs text-gray-400 mt-3">
              Sua senha de acesso vem junto com o pedido. Não recebeu? Fale com a gente no WhatsApp.
            </p>
          </form>
        </div>

        {/* Resultados */}
        <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
          {!searched && (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-50 rounded-2xl mx-auto mb-4">
                <i className="ri-truck-line text-2xl text-[#E8610A]"></i>
              </div>
              <p className="text-gray-500 text-sm max-w-md mx-auto leading-relaxed">
                Assim que você consultar, aparece aqui uma linha do tempo completa
                com cada etapa do seu pedido, da fábrica até a entrega.
              </p>
            </div>
          )}

          {searched && error && !hasResults && (
            <div className="bg-orange-50 border border-[#E8610A]/20 rounded-2xl p-8 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-white rounded-full mx-auto mb-4 border border-[#E8610A]/20">
                <i className="ri-error-warning-line text-xl text-[#E8610A]"></i>
              </div>
              <p className="text-gray-700 text-sm max-w-md mx-auto leading-relaxed">{error}</p>
              <a
                href="https://wa.me/5581982030285?text=Oi%20Ednilson!%20Preciso%20de%20ajuda%20para%20acompanhar%20meu%20pedido."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer mt-4"
              >
                <i className="ri-whatsapp-line"></i>
                Falar no WhatsApp
              </a>
            </div>
          )}

          {hasResults && (
            <div className="space-y-8">
              {orders.map((order) => {
                const details = order.order_id ? orderDetails?.[order.order_id] : undefined;

                return (
                  <div
                    key={order.id}
                    className="bg-white rounded-2xl border border-hop-border overflow-hidden"
                  >
                    {/* Cabeçalho do pedido */}
                    <div className="bg-hop-bg-alt border-b border-hop-border px-6 py-5">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-[#E8610A] uppercase tracking-wider mb-1">
                            Pedido #{details?.id ?? order.id}
                          </p>
                          <h3 className="font-bold text-gray-900 text-base leading-snug">
                            {order.product_description || 'Móveis corporativos'}
                          </h3>
                          {order.customer_name && (
                            <p className="text-sm text-gray-500 mt-1">
                              {order.customer_name}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400 mb-1">Data do pedido</p>
                          <p className="text-sm font-medium text-gray-700">
                            {formatDate(order.created_at)}
                          </p>
                        </div>
                      </div>

                      {/* Badge de status atual */}
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#E8610A] bg-[#E8610A]/10 px-3 py-1.5 rounded-full whitespace-nowrap">
                          <i className="ri-map-pin-line"></i>
                          {STATUS_LABELS[order.status] ?? order.status}
                        </span>
                        {details?.salesperson && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <i className="ri-user-line"></i>
                            {details.salesperson}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Linha do tempo */}
                    <div className="px-6 py-6">
                      <TrackingTimeline order={order} />
                    </div>

                    {/* Detalhes completos do pedido */}
                    {details && (
                      <div className="border-t border-hop-border bg-hop-bg-alt/30 px-6 py-5">
                        <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-file-list-3-line text-[#E8610A]"></i>
                          Detalhes do pedido
                        </h4>

                        {/* Itens */}
                        <div className="space-y-2 mb-4">
                          {details.order_items?.map((it) => (
                            <div
                              key={it.id}
                              className="flex items-center justify-between gap-3 text-sm bg-white rounded-lg border border-hop-border px-3 py-2"
                            >
                              <span className="text-gray-700">
                                <span className="font-semibold text-gray-900">{it.quantity}x</span>{' '}
                                {it.product_name}
                              </span>
                              <span className="text-gray-600 font-medium whitespace-nowrap">
                                {formatBRL(Number(it.subtotal ?? it.quantity * it.unit_price))}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Resumo */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          {details.payment_terms && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <i className="ri-bank-card-line text-gray-400"></i>
                              <span>Pagamento: <strong className="text-gray-900">{details.payment_terms}</strong></span>
                            </div>
                          )}
                          {details.delivery_deadline && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <i className="ri-truck-line text-gray-400"></i>
                              <span>Entrega: <strong className="text-gray-900">{details.delivery_deadline}</strong></span>
                            </div>
                          )}
                          {details.customer_notes && (
                            <div className="flex items-start gap-2 text-gray-600 sm:col-span-2">
                              <i className="ri-sticky-note-line text-gray-400 mt-0.5"></i>
                              <span>Obs: {details.customer_notes}</span>
                            </div>
                          )}
                        </div>

                        {/* Total */}
                        <div className="mt-4 pt-3 border-t border-hop-border flex items-center justify-between">
                          <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                            Total do pedido
                          </span>
                          <span className="text-xl font-bold text-gray-900">
                            {formatBRL(Number(details.subtotal_items ?? 0))}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Agendamento de entrega/montagem */}
                    {SCHEDULABLE_STATUSES.includes(order.status) && (
                      <div className="border-t border-hop-border bg-hop-bg-alt/50 px-6 py-6">
                        <BookingCalendar />
                      </div>
                    )}

                    {/* Recompensas pós-entrega */}
                    {order.status === 'entregue_cliente' && (
                      <div className="border-t border-hop-border bg-hop-bg-alt/50 px-6 py-6">
                        <CompletedReward order={order} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Ajuda */}
          <div className="text-center mt-12 border-t border-hop-border pt-10">
            <p className="text-gray-500 text-sm">
              Não encontrou seu pedido ou precisa de ajuda?{' '}
              <a
                href="https://wa.me/5581982030285?text=Oi%20Ednilson!%20Preciso%20de%20ajuda%20para%20acompanhar%20meu%20pedido."
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E8610A] font-semibold hover:underline cursor-pointer"
              >
                Fale com a gente no WhatsApp
              </a>
            </p>
          </div>
        </div>
      </div>

      <Footer />
      <a
        href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200"
        aria-label="WhatsApp"
      >
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}